import React, { FC } from 'react';
import SbEditable from 'storyblok-react';
import {
    AccordionStoryblok,
    AdvantagesStoryblok,
    ButtonStoryblok,
    HeaderHomeStoryblok,
    HeaderLiveSearchStoryblok,
    HeaderStoryblok,
    HowItWorksStoryblok,
    HubspotFormStoryblok,
    LinkStoryblok,
    MiniFaqStoryblok,
    RichtextStoryblok,
    SpacerStoryblok,
    TeaserStoryblok,
    TextImageStoryblok,
    TopicSelectorStoryblok,
    ZoovuStoryblok,
} from '../../../_shared/interfaces/storyblok';
import HomeAdvantages from '../../home/advantages/advantages';
import HeaderHome from '../../home/header-home/header-home';
import HeaderLiveSearch from '../../home/header-live-search/header-live-search';
import HomeHowItWorks from '../../home/how-it-works/how-it-works';
import HomeMiniFaq from '../../home/mini-faq/mini-faq';
import Accordion from '../accordion/accordion';
import Header from '../header/header';
import StoryblokButton from '../hs-button/storyblok-button';
import StoryblokLink from '../hs-link/storyblok-link';
import Richtext from '../richtext/richtext';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import HubspotForm from '../hubspot-form/hubspot-form';
import Spacer from '../spacer/spacer';
import Teaser from '../teaser/teaser';
import TextImage from '../text-image/text-image';
import Zoovu from '../zoovu/zoovu';
import TopicSelector from '../../home/topic-selector/topic-selector';

/* Register new dynamic components names here */
const components = [
    'header',
    'header_home',
    'header_live_search',
    'mini_faq',
    'advantages',
    'how_it_works',
    'richtext',
    'hubspot_form',
    'button',
    'link',
    'zoovu',
    'text_image',
    'spacer',
    'accordion',
    'topic_selector',
    'teaser',
] as const;

export type ComponentName = (typeof components)[number];

type DynamicComponentBlok = {
    component: ComponentName;
} & (
    | HeaderHomeStoryblok
    | HeaderStoryblok
    | HeaderLiveSearchStoryblok
    | AdvantagesStoryblok
    | HowItWorksStoryblok
    | MiniFaqStoryblok
    | TextImageStoryblok
    | TopicSelectorStoryblok
    | SpacerStoryblok
    | AccordionStoryblok
    | ZoovuStoryblok
    | ButtonStoryblok
    | LinkStoryblok
    | HubspotFormStoryblok
    | RichtextStoryblok
    | TeaserStoryblok
);

export type DynamicComponentProps = {
    blok: DynamicComponentBlok;
    storyblokCommon: StoryblokCommon;
};

const Components: Record<ComponentName, FC<DynamicComponentProps>> = {
    header: Header as FC<DynamicComponentProps>,
    header_home: HeaderHome as FC<DynamicComponentProps>,
    header_live_search: HeaderLiveSearch as FC<DynamicComponentProps>,
    mini_faq: HomeMiniFaq as FC<DynamicComponentProps>,
    advantages: HomeAdvantages as FC<DynamicComponentProps>,
    how_it_works: HomeHowItWorks as FC<DynamicComponentProps>,
    richtext: Richtext as FC<DynamicComponentProps>,
    hubspot_form: HubspotForm as FC<DynamicComponentProps>,
    button: StoryblokButton as FC<DynamicComponentProps>,
    link: StoryblokLink as FC<DynamicComponentProps>,
    zoovu: Zoovu as FC<DynamicComponentProps>,
    text_image: TextImage as FC<DynamicComponentProps>,
    spacer: Spacer as FC<DynamicComponentProps>,
    accordion: Accordion as FC<DynamicComponentProps>,
    topic_selector: TopicSelector as FC<DynamicComponentProps>,
    teaser: Teaser as FC<DynamicComponentProps>,
};

const DynamicComponent: FC<DynamicComponentProps> = ({ blok, storyblokCommon }) => {
    const componentName = blok.component as ComponentName;
    if (typeof Components[componentName] !== 'undefined') {
        const Component = Components[componentName];
        return (
            <SbEditable content={blok}>
                <Component blok={blok} storyblokCommon={storyblokCommon} />
            </SbEditable>
        );
    }
    return (
        <p>
            The component <strong>{blok.component}</strong> has not been created yet.
        </p>
    );
};

export default DynamicComponent;
